<template>
  <div class="flex flex-col min-h-screen">
    <main class="flex-1 py-12 md:py-24 lg:py-32">
      <div class="container px-4 md:px-6 mx-auto max-w-4xl">
        <!-- Header -->
        <div class="mb-8">
          <h1 class="text-4xl font-heading font-bold tracking-tighter sm:text-5xl text-graphite dark:text-white mb-4">
            {{ $t('terms.title') }}
          </h1>
          <p class="text-sm font-body text-graphite dark:text-gray-light">
            {{ $t('terms.lastUpdated') }}: {{ $t('terms.updateDate') }}
          </p>
        </div>

        <!-- Content -->
        <div class="prose prose-lg dark:prose-invert max-w-none space-y-8">
          <!-- Introducción -->
          <section class="space-y-4">
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('terms.introduction') }}
            </p>
          </section>

          <!-- Aceptación de términos -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('terms.sections.acceptance.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('terms.sections.acceptance.description') }}
            </p>
          </section>

          <!-- Uso del sitio -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('terms.sections.usage.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('terms.sections.usage.description') }}
            </p>
            <ul class="list-disc list-inside space-y-2 font-body text-graphite dark:text-gray-light ml-4">
              <li v-for="(item, index) in usageItems" :key="index">
                {{ item }}
              </li>
            </ul>
          </section>

          <!-- Propiedad intelectual -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('terms.sections.intellectual.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('terms.sections.intellectual.description') }}
            </p>
            <ul class="list-disc list-inside space-y-2 font-body text-graphite dark:text-gray-light ml-4">
              <li v-for="(item, index) in intellectualItems" :key="index">
                {{ item }}
              </li>
            </ul>
          </section>

          <!-- Contenido del usuario -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('terms.sections.userContent.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('terms.sections.userContent.description') }}
            </p>
          </section>

          <!-- Enlaces a terceros -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('terms.sections.links.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('terms.sections.links.description') }}
            </p>
          </section>

          <!-- Limitación de responsabilidad -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('terms.sections.liability.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('terms.sections.liability.description') }}
            </p>
          </section>

          <!-- Indemnización -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('terms.sections.indemnification.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('terms.sections.indemnification.description') }}
            </p>
          </section>

          <!-- Modificaciones -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('terms.sections.modifications.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('terms.sections.modifications.description') }}
            </p>
          </section>

          <!-- Ley aplicable -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('terms.sections.law.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('terms.sections.law.description') }}
            </p>
          </section>

          <!-- Contacto -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('terms.sections.contact.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('terms.sections.contact.description') }}
            </p>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              <router-link 
                to="/contact" 
                class="text-primary hover:underline"
              >
                {{ $t('terms.sections.contact.link') }}
              </router-link>
            </p>
          </section>
        </div>

        <!-- Back to home button -->
        <div class="mt-12 pt-8 border-t border-gray-light dark:border-graphite-light">
          <router-link 
            to="/" 
            class="inline-flex items-center px-6 py-3 bg-primary hover:bg-primary-dark text-graphite font-body font-medium rounded-md transition-colors"
          >
            {{ $t('terms.backToHome') }}
          </router-link>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const usageItems = computed(() => {
  const items = t('terms.sections.usage.items')
  return Array.isArray(items) ? items : []
})

const intellectualItems = computed(() => {
  const items = t('terms.sections.intellectual.items')
  return Array.isArray(items) ? items : []
})
</script>

<style scoped>
.container {
  max-width: 1200px;
}

.prose {
  color: inherit;
}

.prose h2 {
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.prose ul {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.prose li {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
</style>
